import React, { useState, useEffect } from "react";
import '../../index.css';

const initialClasses = [
  { id: 1, name: "Class 1", section: "A", teacher: "John Doe", status: "Active" },
  { id: 2, name: "Class 2", section: "B", teacher: "Jane Smith", status: "Inactive" },
  { id: 3, name: "Class 3", section: "A", teacher: "David Lee", status: "Active" },
];

export default function Class() {

  const STORAGE_KEY = "classes";
  const [classList, setClassList] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    section: "",
    teacher: "",
    status: "Active"
  });

  // 🔍 FILTER
  const filteredClasses = classList.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.section.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.teacher.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ADD
  const openAddModal = () => {
    setIsEdit(false);
    setFormData({
      id: "",
      name: "",
      section: "",
      teacher: "",
      status: "Active"
    });
    setShowModal(true);
  };

  // UPDATE
  const openEditModal = () => {
    if (!selectedClass) {
      alert("Select a class first");
      return;
    }
    setIsEdit(true);
    setFormData(selectedClass);
    setShowModal(true);
  };

  // DELETE
  const handleDelete = () => {
    if (!selectedClass) {
      alert("Select a class first");
      return;
    }

    const updated = classList.filter(
      (c) => c.id !== selectedClass.id
    );

    setClassList(updated);

    //  SAVE
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));

    setSelectedClass(null);
  };

  // SAVE
  const handleSave = () => {

    if (!formData.name || !formData.section || !formData.teacher) {
      alert("All fields required");
      return;
    }

    let updatedList = [];

    if (isEdit) {
      updatedList = classList.map((c) =>
        c.id === formData.id ? formData : c
      );
    } else {
      const newClass = {
        ...formData,
        id: Date.now(),
      };

      updatedList = [newClass, ...classList];
    }

    setClassList(updatedList);

    //  SAVE to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedList));

    //  sync across tabs/pages
    window.dispatchEvent(new Event("storage"));

    setShowModal(false);
    setSelectedClass(null);
  };

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];

    if (saved.length === 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initialClasses));
      setClassList(initialClasses);
    } else {
      setClassList(saved);
    }
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      setClassList(saved);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <>
      <style>{`
        .page { background:#f5f7fb; padding:20px; }
        .card { background:#fff; padding:15px; border-radius:8px; }

        .header { display:flex; justify-content:space-between; margin-bottom:10px; }

        .controls { display:flex; gap:10px; margin-bottom:10px; flex-wrap:wrap; }

        .search-box {
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }

        .btn { padding:7px 12px; border:none; border-radius:4px; cursor:pointer; }
        .btn-dark { background:#2c3e50; color:white; }
        .btn-blue { background:#3498db; color:white; }
        .btn-red { background:#e74c3c; color:white; }

        table { width:100%; border-collapse:collapse; }
        th, td { padding:10px; border-bottom:1px solid #eee; }

        tr:hover { background:#f4f6f9; cursor:pointer; }
        .active-row { background:#eef3ff; }

        .status { color:green; }

        input[type="checkbox"] {
          cursor:pointer;
        }

        /* MODAL */
        .modal {
          position:fixed;
          top:0;
          left:0;
          width:100%;
          height:100%;
          background:rgba(0,0,0,0.5);
          display:flex;
          justify-content:center;
          align-items:center;
        }

        .modal-content {
          background:white;
          padding:20px;
          border-radius:8px;
          width:350px;
        }

        .modal-content input,
        .modal-content select {
          width:100%;
          margin-bottom:10px;
          padding:8px;
        }
      `}</style>

      <div className="page">
        <div className="card">

          {/* HEADER */}
          <div className="header">
            <h3>Class</h3>
            <div>{classList.length} Records</div>
          </div>

          {/* CONTROLS */}
          <div className="controls">

            <input
              className="search-box"
              placeholder="Search class, section, teacher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <button className="btn btn-dark" onClick={openAddModal}>
              + Add Class
            </button>

            <button className="btn btn-blue" onClick={openEditModal}>
              Update
            </button>

            <button className="btn btn-red" onClick={handleDelete}>
              Delete
            </button>
          </div>

          {/* TABLE */}
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Class</th>
                <th>Section</th>
                <th>Teacher</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {filteredClasses.map((c) => (
                <tr
                  key={c.id}
                  className={selectedClass?.id === c.id ? "active-row" : ""}
                  onClick={() =>
                    setSelectedClass(
                      selectedClass?.id === c.id ? null : c
                    )
                  }
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedClass?.id === c.id}
                      onChange={() =>
                        setSelectedClass(
                          selectedClass?.id === c.id ? null : c
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>

                  <td>{c.name}</td>
                  <td>{c.section}</td>
                  <td>{c.teacher}</td>
                  <td className="status">{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>

        {/* MODAL */}
        {showModal && (
          <div className="modal">
            <div className="modal-content">

              <h3>{isEdit ? "Update Class" : "Add Class"}</h3>

              <input
                placeholder="Class Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />

              <input
                placeholder="Section"
                value={formData.section}
                onChange={(e) =>
                  setFormData({ ...formData, section: e.target.value })
                }
              />

              <input
                placeholder="Teacher Name"
                value={formData.teacher}
                onChange={(e) =>
                  setFormData({ ...formData, teacher: e.target.value })
                }
              />

              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>

              <div style={{ display: "flex", gap: "10px" }}>
                <button className="btn btn-blue" onClick={handleSave}>
                  Save
                </button>

                <button
                  className="btn btn-red"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </>
  );
}